import type { AnyStateFrame, GameContract } from '@efira/game-sdk';
import {
  CHANNELS,
  PROTOCOL_VERSION,
  SYSTEM_EVENTS,
  decode,
  type ActionCommand,
  type ChatLine,
  type DirectorMessagePayload,
  type GameResyncPayload,
  type HelloPayload,
  type LayoutSpecPayload,
  type RejectedPayload,
  type SoundCommand,
  type StageCommand,
  type StageStatePayload,
  type DirectoryMember,
  type SessionConfigPayload,
  type ViewerEvent,
  type WelcomePayload,
} from '@efira/protocol';
import { io, type Socket } from 'socket.io-client';

import { createBus, type OverlayBus } from './bus.js';

/**
 * Соединение overlay с сервером.
 *
 * Токен берётся из адреса: browser source открывается без участия человека, и
 * другого секрета у него нет. Отозванный токен сервер не принимает - в этом
 * смысл ротации.
 *
 * Переподключение отдано Socket.IO: он делает это с возрастающей задержкой,
 * что для browser source в OBS правильнее, чем цикл каждые 100 мс при
 * упавшем сервере. При восстановлении конфигурация приходит заново и
 * целиком - состояние не собирается по кусочкам из журнала.
 */

export type ConnectionStatus =
  | { kind: 'connecting' }
  | { kind: 'connected' }
  | { kind: 'reconnecting' }
  | { kind: 'rejected'; reason: RejectedPayload };

export interface OverlayConnection {
  socket: Socket;
  /** Шина каналов игры. Живёт столько же, сколько соединение. */
  bus: OverlayBus;
  close: () => void;
}

export interface ConnectionHandlers {
  onStatus: (status: ConnectionStatus) => void;
  onSessionConfig: (config: SessionConfigPayload) => void;
  /** Поток событий зрителей. Лента и реакция на них - Фаза 4. */
  onViewerEvent?: (event: ViewerEvent) => void;
  /** Справочник участников: имя и аватар приходят один раз на зрителя. */
  onMembers?: (members: Record<string, DirectoryMember>) => void;
  /** Команда действия: то, что сцена исполняет. Правил привязок здесь нет. */
  onAction?: (command: ActionCommand) => void;
  /** Прямой чат - только для сцен, объявивших `chatAccess.direct`. */
  onChatLine?: (line: ChatLine) => void;
  /**
   * Сообщение ведущего зрителям.
   *
   * До сцены НЕ доходит: текст рисует overlay поверх неё и снимает по
   * истечении длительности. Иначе каждая из десяти игр была бы обязана уметь
   * показывать чужой текст.
   */
  onDirectorMessage?: (message: DirectorMessagePayload) => void;
  /**
   * Команда звука: проиграть файл или оборвать всё.
   *
   * Звук принадлежит ПЛАТФОРМЕ, а не сцене: пад звучит и в паузе, и между
   * раундами, и когда сцена не выбрана вовсе.
   */
  onSound?: (command: SoundCommand) => void;
  /** Команда сцене платформы: гифка, оповещение, эффект, озвучка, музыка. */
  onStage?: (command: StageCommand) => void;
  /**
   * Как отправить состояние сцены серверу.
   *
   * Ставится соединением при подключении: сцена сокета не видит - то же
   * правило, что у ресинхронизации и состава элементов.
   */
  onStageReport?: (send: (payload: StageStatePayload) => void) => void;
}

/**
 * Открыт ли overlay в кадре консоли.
 *
 * Признак берётся из адреса: консоль встраивает `/s/<токен>?preview=1`.
 * Различать по заголовку `Referer` нельзя - он снят политикой `no-referrer`
 * ровно потому, что в адресе overlay лежит токен.
 */
export function isPreview(): boolean {
  return new URLSearchParams(globalThis.location?.search ?? '').get('preview') === '1';
}

/** Токен из адреса вида /s/<token>. */
export function tokenFromLocation(pathname: string): string | null {
  const match = /^\/s\/([A-Za-z0-9_-]+)\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

export function connectOverlay(
  apiUrl: string,
  token: string,
  handlers: ConnectionHandlers,
  /**
   * Шина каналов игры. Передаётся снаружи, а не создаётся здесь: её время
   * жизни - время жизни компонента overlay, а не эффекта соединения.
   * Созданная внутри, она потребовала бы вернуть её через состояние React,
   * то есть вызвать setState прямо в теле эффекта и получить каскад
   * перерисовок - а вместе с ним перемонтирование игры на пустом месте.
   */
  bus: OverlayBus = createBus(),
): OverlayConnection {
  const socket = io(apiUrl, {
    // Сразу WebSocket: polling-фаза стоит лишнего round-trip на старте, а
    // overlay открывается в том числе с телефона на мобильном канале.
    transports: ['websocket'],
    autoConnect: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 10_000,
  });

  bus.setResync((payload: GameResyncPayload) => {
    socket.emit(CHANNELS.gameResync, payload);
  });

  bus.setReportLayout((payload: LayoutSpecPayload) => {
    socket.emit(CHANNELS.gameLayoutSpec, payload);
  });

  const hello: HelloPayload = {
    protocol: PROTOCOL_VERSION,
    role: 'overlay',
    token,
    /* Кадр консоли помечает себя признаком, чтобы не считаться зрительским
       подключением. Признак приходит адресом: `?preview=1`. */
    ...(isPreview() ? { preview: true } : {}),
  };

  socket.on('connect', () => {
    // Рукопожатие отправляется при каждом соединении, включая переподключение.
    // Сервер обрабатывает повтор идемпотентно - у конкурента дубль overlay:join
    // выглядит дефектом (docs/research/STACK_REPORT.md, 3.5).
    socket.emit(SYSTEM_EVENTS.hello, hello);
  });

  socket.on(SYSTEM_EVENTS.welcome, (raw: unknown) => {
    const welcome = decode<WelcomePayload>(raw);

    if (welcome !== null) {
      handlers.onStatus({ kind: 'connected' });
    }
  });

  socket.on(SYSTEM_EVENTS.rejected, (raw: unknown) => {
    const rejected = decode<RejectedPayload>(raw);

    if (rejected !== null) {
      // Отказ - не повод переподключаться: токен отозван или версия
      // несовместима, и повтор ничего не изменит, кроме нагрузки на сервер.
      socket.disconnect();
      handlers.onStatus({ kind: 'rejected', reason: rejected });
    }
  });

  socket.on(CHANNELS.sessionConfig, (raw: unknown) => {
    const config = decode<SessionConfigPayload>(raw);

    if (config !== null) {
      handlers.onSessionConfig(config);
    }
  });

  socket.on(CHANNELS.viewerEvent, (raw: unknown) => {
    const event = decode<ViewerEvent>(raw);

    if (event !== null) {
      handlers.onViewerEvent?.(event);
    }
  });

  socket.on(CHANNELS.gameAction, (raw: unknown) => {
    const command = decode<ActionCommand>(raw);

    if (command !== null) {
      // Сцена получает ГОТОВУЮ КОМАНДУ, а не сырое событие: правила привязок
      // до overlay не доходят вообще. Именно поэтому игровой чанк весит
      // килобайты, а не мегабайты.
      handlers.onAction?.(command);
      bus.pushAction(command);
    }
  });

  socket.on(CHANNELS.gameChat, (raw: unknown) => {
    const line = decode<ChatLine>(raw);

    if (line !== null) {
      handlers.onChatLine?.(line);
      bus.pushChat(line);
    }
  });

  socket.on(CHANNELS.directoryMembers, (raw: unknown) => {
    const payload = decode<{ members: Record<string, DirectoryMember> }>(raw);

    if (payload !== null) {
      handlers.onMembers?.(payload.members);
      bus.pushMembers(payload.members);
    }
  });

  socket.on(CHANNELS.directorMessage, (raw: unknown) => {
    const message = decode<DirectorMessagePayload>(raw);

    if (message !== null) {
      handlers.onDirectorMessage?.(message);
    }
  });

  handlers.onStageReport?.((payload: StageStatePayload) => {
    socket.emit(CHANNELS.stageState, payload);
  });

  socket.on(CHANNELS.stageCommand, (raw: unknown) => {
    const command = decode<StageCommand>(raw);

    if (command !== null) {
      handlers.onStage?.(command);
    }
  });

  socket.on(CHANNELS.soundCommand, (raw: unknown) => {
    const command = decode<SoundCommand>(raw);

    if (command !== null) {
      handlers.onSound?.(command);
    }
  });

  socket.on(CHANNELS.gameContract, (raw: unknown) => {
    const contract = decode<GameContract>(raw);

    if (contract !== null) {
      bus.pushContract(contract);
    }
  });

  socket.on(CHANNELS.gameState, (raw: unknown) => {
    const frame = decode<AnyStateFrame>(raw);

    if (frame !== null) {
      bus.pushState(frame);
    }
  });

  socket.on('connect', () => {
    // После переподключения мир накопленных дельт недействителен: пока сокета
    // не было, сервер продолжал считать. Полный снимок запрашивается сразу, а
    // не по факту разрыва последовательности тиков - иначе первый же кадр
    // после обрыва лёг бы на устаревший мир и дал бы ТИХО неверную картинку.
    bus.resync('reconnect', null);
  });

  socket.on('disconnect', (reason: string) => {
    // `io client disconnect` - это наш собственный вызов после отказа;
    // сообщать о нём как о потере связи неправильно.
    if (reason !== 'io client disconnect') {
      handlers.onStatus({ kind: 'reconnecting' });
    }
  });

  handlers.onStatus({ kind: 'connecting' });

  return {
    socket,
    bus,
    close: () => {
      socket.disconnect();
      bus.dispose();
    },
  };
}
